import { getProjectBridgeDetailPath } from "./project-bridge-route-paths"

export function getProjectBridgeRewriteCardTitle(section) {
    if (section?.changeType === "intro_content") return "Introduction"

    const taskPrefix = buildTaskPrefix(section)
    if (section?.changeType === "task_note") return `${taskPrefix} Notes`
    return taskPrefix
}

export function getProjectBridgeRewriteEmptyState(section) {
    if (section?.mappingStatus === "unresolved") return "Mapping unresolved"
    return "No rewrite generated yet"
}

export function getProjectBridgeCanonicalStatusLabel(section) {
    if (section?.canonicalMatchKind === "new") return "Shared rewrite"
    if (section?.canonicalMatchKind === "exact_reuse") return "Shared rewrite"
    if (section?.canonicalMatchKind === "variant") return "Similar rewrite"
    if (section?.canonicalMatchKind === "unavailable") return "Unavailable"
    return ""
}

export function getProjectBridgeCanonicalStatusChipLabel(section) {
    const statusLabel = getProjectBridgeCanonicalStatusLabel(section)
    if (!statusLabel) return ""
    if (hasProjectBridgeCanonicalUsagePopover(section)) {
        const usageLabel = getProjectBridgeCanonicalUsageCountLabel(section)
        if (!usageLabel) return statusLabel
        return `${statusLabel} (${usageLabel})`
    }

    if (isProjectBridgeCanonicalUsageMatchKind(section?.canonicalMatchKind)) return ""
    return statusLabel
}

export function getProjectBridgeCanonicalStatusChipAriaLabel(section) {
    if (!hasProjectBridgeCanonicalUsagePopover(section)) return ""

    const label = getProjectBridgeCanonicalStatusChipLabel(section)
    if (!label) return ""
    return `Open linked schedules for ${label}`
}

export function getProjectBridgeCanonicalStatusChipVariant(section) {
    if (hasProjectBridgeCanonicalUsagePopover(section)) return "filled"
    return "outlined"
}

export function getProjectBridgeCanonicalPropagationStatusLabel(section) {
    const propagation = section?.canonicalPropagation
    if (propagation?.status === "needs_review") return "Shared rewrite changed - review"
    if (propagation?.status === "skipped" && propagation?.reason !== "source") return "Shared rewrite kept"
    return ""
}

export function getProjectBridgeCanonicalPropagationStatusColor(section) {
    const propagation = section?.canonicalPropagation
    if (propagation?.status === "auto_applied") return "success"
    if (propagation?.status === "needs_review") return "warning"
    return "default"
}

export function hasProjectBridgeCanonicalUsagePopover(section) {
    if (!isProjectBridgeCanonicalUsageMatchKind(section?.canonicalMatchKind)) return false
    if (Number(section?.canonicalUsage?.otherCount || 0) <= 0) return false
    return Array.isArray(section?.canonicalUsage?.items) && section.canonicalUsage.items.length > 1
}

export function hasProjectBridgeSharedRewriteEditWarning(section) {
    return hasProjectBridgeCanonicalUsagePopover(section)
}

export function getProjectBridgeSharedRewriteEditWarning(section) {
    if (!hasProjectBridgeSharedRewriteEditWarning(section)) return ""
    const count = Number(section?.canonicalUsage?.otherCount || 0)
    const scheduleLabel = count === 1 ? "1 other schedule" : `${count} other schedules`
    return `This rewrite is also used by ${scheduleLabel}. Saving as shared will update matching pending and accepted rewrites that have not been customised.`
}

export function getProjectBridgeSharedRewriteSaveLabel(section) {
    if (hasProjectBridgeSharedRewriteEditWarning(section)) return "Save shared rewrite"
    return "Save"
}

export function getProjectBridgeLocalRewriteSaveLabel(section) {
    if (hasProjectBridgeSharedRewriteEditWarning(section)) return "Save only this schedule"
    return ""
}

export function getProjectBridgeCanonicalUsageCountLabel(section) {
    const totalCount = Number(section?.canonicalUsage?.totalCount || 0)
    if (totalCount <= 0) return ""
    return `${totalCount}`
}

export function getProjectBridgeCanonicalUsageDefinition(section) {
    if (isProjectBridgeCanonicalUsageMatchKind(section?.canonicalMatchKind)) {
        return "This wording is linked across Project Bridge schedules. Editing it can update matching pending and accepted rewrites that have not been customised."
    }

    if (section?.canonicalMatchKind === "variant") {
        return "This rewrite is similar to shared wording used elsewhere but is not linked for automatic updates."
    }

    return "Shared rewrite matching metadata is available for this item."
}

export function getProjectBridgeCanonicalUsageRelationshipLabel(item) {
    if (isProjectBridgeCanonicalUsageMatchKind(item?.canonicalMatchKind)) return "Shared"
    if (item?.canonicalMatchKind === "variant") return "Similar"
    if (isSharedRelationshipLabel(item?.relationshipLabel)) return "Shared"
    if (item?.relationshipLabel === "Variant") return "Similar"
    return "Related"
}

export function getProjectBridgeCanonicalUsagePath(item, topLevelPath) {
    const overlapId = `${item?.overlapId || ""}`.trim()
    if (!overlapId) return ""

    const basePath = getProjectBridgeDetailPath(overlapId, topLevelPath)
    const changeId = `${item?.changeId || ""}`.trim()
    if (!changeId) return basePath
    return `${basePath}?changeId=${encodeURIComponent(changeId)}`
}

export function hasProjectBridgeCanonicalPropagationReview(section) {
    return (
        section?.canonicalPropagation?.status === "needs_review" &&
        Boolean(`${section?.canonicalPropagation?.proposedText || ""}`.trim())
    )
}

export function getProjectBridgeCanonicalPropagationProposedText(section) {
    return section?.canonicalPropagation?.proposedText || ""
}

export function getProjectBridgeProposedDisplayText(section) {
    return section?.displayProposedText ?? section?.proposedText ?? ""
}

function buildTaskPrefix(section) {
    const taskTitle = `${section?.taskTitle || ""}`.trim()
    if (section?.taskNumber && taskTitle) return `${section.taskNumber}. ${taskTitle}`
    if (section?.taskNumber) return `Task ${section.taskNumber}`
    return section?.label || "Task"
}

function isProjectBridgeCanonicalUsageMatchKind(canonicalMatchKind) {
    return canonicalMatchKind === "new" || canonicalMatchKind === "exact_reuse"
}

function isSharedRelationshipLabel(relationshipLabel) {
    return relationshipLabel === "Source" || relationshipLabel === "Reused"
}
